/**
 * iter-300 · Brand Portfolio Switcher
 * One-click preset bundles that swap hinge/straightener/telescopic/sliding/
 * handle brands + rates in the Storage Estimator. Great for quoting the same
 * module at multiple price tiers.
 */
import { useState } from 'react';
import { Check, Layers, FileText } from 'lucide-react';
import { BRAND_PORTFOLIO_PRESETS, resolvePreset } from './storageConfig';

const TONE_CLASSES = {
  amber: 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900',
  blue: 'border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-900',
  emerald: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900',
  stone: 'border-stone-300 bg-stone-50 hover:bg-stone-100 text-stone-800',
};

const ACTIVE_TONE = {
  amber: 'ring-2 ring-amber-500 bg-amber-100',
  blue: 'ring-2 ring-blue-500 bg-blue-100',
  emerald: 'ring-2 ring-emerald-500 bg-emerald-100',
  stone: 'ring-2 ring-stone-600 bg-stone-200',
};

export function BrandPortfolioSwitcher({ onApply, onCompareTiers, comparing, tierSummaries }) {
  const [active, setActive] = useState(null);

  const handleApply = (preset) => {
    const resolved = resolvePreset(preset.id);
    if (!resolved) return;
    setActive(preset.id);
    onApply?.(resolved);
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="brand-portfolio-switcher">
      <div className="flex items-center gap-2 mb-1">
        <Layers size={16} className="text-stone-700" />
        <h3 className="text-sm font-bold text-stone-800">Brand Portfolio</h3>
        <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold bg-stone-100 px-1.5 py-0.5 rounded">
          1-click tier swap
        </span>
        {onCompareTiers && (
          <div className="ml-auto flex items-center gap-1.5" data-testid="tier-compare-actions">
            <span className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold mr-0.5">Compare PDF:</span>
            <button
              type="button"
              onClick={() => onCompareTiers('client')}
              disabled={comparing}
              className="text-xs font-semibold text-blue-700 hover:text-blue-900 border border-blue-300 hover:bg-blue-50 rounded-md px-2 py-1 flex items-center gap-1 transition-colors disabled:opacity-50"
              data-testid="compare-tiers-pdf-client-btn"
              title="Client view — hides vendor cost / markup / margin"
            >
              <FileText size={12} />Client
            </button>
            <button
              type="button"
              onClick={() => onCompareTiers('vendor')}
              disabled={comparing}
              className="text-xs font-semibold text-purple-700 hover:text-purple-900 border border-purple-300 hover:bg-purple-50 rounded-md px-2 py-1 flex items-center gap-1 transition-colors disabled:opacity-50"
              data-testid="compare-tiers-pdf-vendor-btn"
              title="Vendor view — hides client base / pre-GST / final / margin"
            >
              <FileText size={12} />Vendor
            </button>
            <button
              type="button"
              onClick={() => onCompareTiers('admin')}
              disabled={comparing}
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-300 hover:bg-emerald-50 rounded-md px-2 py-1 flex items-center gap-1 transition-colors disabled:opacity-50"
              data-testid="compare-tiers-pdf-admin-btn"
              title="Admin view — full breakdown including markup & margin"
            >
              <FileText size={12} />{comparing ? '…' : 'Admin'}
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-stone-500 mb-3">
        Quote the same module at different price tiers by swapping the full hardware bundle at once.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {BRAND_PORTFOLIO_PRESETS.map((p) => {
          const tone = TONE_CLASSES[p.tone] || TONE_CLASSES.stone;
          const activeCls = active === p.id ? ACTIVE_TONE[p.tone] : '';
          // Live mini-total per preset (if tierSummaries supplied)
          const tier = (tierSummaries || []).find(t => t.id === p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => handleApply(p)}
              className={`group relative text-left rounded-lg border px-3 py-2.5 transition-all ${tone} ${activeCls}`}
              data-testid={`brand-portfolio-${p.id}`}
              title={p.description}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold">{p.label}</span>
                {active === p.id && <Check size={14} className="shrink-0" />}
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
                {p.tag}
              </span>
              <p className="text-[11px] mt-1 leading-snug opacity-80 line-clamp-2">
                {p.description}
              </p>
              {tier && tier.finalClient > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-current border-opacity-20">
                  <span className="text-[10px] opacity-70 font-semibold">Client all-in:</span>
                  <span className="ml-1 text-xs font-mono font-bold" data-testid={`tier-mini-total-${p.id}`}>
                    Rs {Math.round(tier.finalClient).toLocaleString('en-IN')}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
